import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { VIPSection } from "@/components/seating/VIPSection";
import { StandingSection } from "@/components/seating/StandingSection";
import { VIPSeatingTemplate } from "@/components/seating/VIPSeatingTemplate";
import { SeatingHeader } from "@/components/seating/SeatingHeader";
import { PaymentSection } from "@/components/seating/PaymentSection";
import { Event } from "@/lib/types";
import { useIsMobile } from "@/hooks/use-mobile";
import { api } from "@/components/client/backend";

const SeatingPlan = () => {
  const { seoUrl } = useParams<{ seoUrl: string }>();
  const [selectedSeats, setSelectedSeats] = useState<string[]>([]);
  const [vipTicketCount, setVipTicketCount] = useState(0);
  const [standingTickets, setStandingTickets] = useState(0);
  const [earlyBirdTickets, setEarlyBirdTickets] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const isMobile = useIsMobile();

  // Restore checkout data from localStorage if available
  useEffect(() => {
    const savedData = localStorage.getItem("checkoutData");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);

        // Check if the data is for the current event and not too old (24 hours)
        const isValid =
          parsedData.eventId === event?.id &&
          new Date().getTime() - new Date(parsedData.timestamp).getTime() < 24 * 60 * 60 * 1000;

        if (isValid) {
          console.log("Restoring saved checkout data:", parsedData);

          // Restore ticket selections
          if (parsedData.tickets) {
            setSelectedSeats(parsedData.tickets.vipSeats || []);
            setStandingTickets(parsedData.tickets.standingTickets || 0);
          }

          toast.success("Previous selections restored");
        } else {
          // Clear invalid or expired data
          localStorage.removeItem("checkoutData");
        }
      } catch (error) {
        console.error("Error restoring checkout data:", error);
        localStorage.removeItem("checkoutData");
      }
    }
  }, []);

  const { data: event, isLoading, error } = useQuery({
    queryKey: ["event", seoUrl],
    queryFn: async () => {
      console.log("Fetching event with SEO URL:", seoUrl);

      if (!seoUrl) {
        console.error("No event SEO URL provided");
        throw new Error("No event SEO URL provided");
      }

      const resp = await api.get(`events/${seoUrl}`)
      if (!resp.ok) {
        throw new Error("Event not found");
      }
      return await resp.json() as Event;
    },
    enabled: !!seoUrl,
    meta: {
      onError: (error: Error) => {
        console.error("Error fetching event:", error);
        toast.error("Failed to load event details");
      }
    }
  });

  const handleSeatClick = useCallback((seatId: string) => {
    setSelectedSeats(prev =>
      prev.includes(seatId)
        ? prev.filter(seat => seat !== seatId)
        : [...prev, seatId]
    );
  }, []);

  const handleVipTicketChange = useCallback((increment: boolean) => {
    if (!event) return;

    setVipTicketCount(prev => {
      if (increment) {
        if (prev >= (event.quantityVip || 0)) {
          toast.error("No more VIP tickets available");
          return prev;
        }
        return prev + 1;
      } else {
        return Math.max(0, prev - 1);
      }
    });
  }, [event]);

  const handleStandingTicketChange = useCallback((increment: boolean) => {
    if (!event) return;

    if (increment) {
      if (standingTickets >= (event.quantityStanding || 0)) {
        toast.error("No more standing tickets available");
        return;
      }
      setStandingTickets(prev => prev + 1);
    } else {
      setStandingTickets(prev => Math.max(0, prev - 1));
    }
  }, [event, standingTickets]);

  const handleEarlyBirdTicketChange = useCallback((increment: boolean) => {
    if (!event) return;

    if (increment) {
      if (earlyBirdTickets >= (event.quantityEarlyBird || 0)) {
        toast.error("No more early bird tickets available");
        return;
      }
      setEarlyBirdTickets(prev => prev + 1);
    } else {
      setEarlyBirdTickets(prev => Math.max(0, prev - 1));
    }
  }, [event, earlyBirdTickets]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 pt-28 pb-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !event || !seoUrl) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 pt-28 pb-12">
          <div className="text-white text-center">
            <h2 className="text-2xl font-bold mb-4">
              {error ? "Error Loading Event" : "Event Not Found"}
            </h2>
            {error && <p>{error.message}</p>}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12">
        <div className="space-y-6 sm:space-y-8">
          <SeatingHeader seoUrl={seoUrl} eventTitle={event.title} />

          <div className="bg-gray-800 p-4 sm:p-6 lg:p-8 rounded-lg">
            {event?.templateType === 'no_seating' ? (
              <>
                <div className="bg-gray-700 p-4 sm:p-6 rounded-lg mb-6 sm:mb-8">
                  <div className="flex flex-col space-y-4">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                      <div className="space-y-2">
                        <h3 className="text-xl sm:text-2xl font-bold text-white">VIP: Meet and Greet</h3>
                        {event.ticketDescriptionVip && (
                          <p className="text-sm sm:text-base text-gray-400 italic">{event.ticketDescriptionVip}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row justify-between gap-6 lg:gap-24">
                  <div className="flex-1 flex justify-center">
                    <VIPSection
                      sectionNumber={1}
                      selectedSeats={selectedSeats}
                      onSeatClick={handleSeatClick}
                      maxSeats={event.quantityVip || 0}
                    />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <VIPSection
                      sectionNumber={2}
                      selectedSeats={selectedSeats}
                      onSeatClick={handleSeatClick}
                      maxSeats={event.quantityVip || 0}
                    />
                  </div>
                </div>

                <StandingSection
                  event={event}
                  standingTickets={standingTickets}
                  maxStandingTickets={event.quantityStanding || 0}
                  earlyBirdTickets={earlyBirdTickets}
                  maxEarlyBirdTickets={event.quantityEarlyBird || 0}
                  onStandingTicketChange={handleStandingTicketChange}
                  onEarlyBirdTicketChange={handleEarlyBirdTicketChange}
                />
              </>
            ) : (
              <VIPSeatingTemplate
                event={event}
                vipTicketCount={vipTicketCount}
                standingTickets={standingTickets}
                earlyBirdTickets={earlyBirdTickets}
                onVipTicketChange={handleVipTicketChange}
                onStandingTicketChange={handleStandingTicketChange}
                onEarlyBirdTicketChange={handleEarlyBirdTicketChange}
              />
            )}
          </div>

          <PaymentSection
            event={event}
            seoUrl={seoUrl}
            selectedSeats={event?.templateType === 'no_seating' ? selectedSeats : []}
            vipTicketCount={event?.templateType === 'vip_seating' ? vipTicketCount : 0}
            standingTickets={standingTickets}
            earlyBirdTickets={earlyBirdTickets}
            discountPercentage={discountPercentage}
            setDiscountPercentage={setDiscountPercentage}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SeatingPlan;